import React from 'react';
import { StyleSheet, View, Text, ActivityIndicator, ScrollView, Platform } from 'react-native';
import { useTheme } from '../../../../theme/useTheme';
import HoursByCategoryTable from './components/HoursByCategoryTable';
import HoursGroupedBarCard from './components/HoursGroupedBarCard';
import {
  type ChartDrillLevel,
  type ChartTimeframeGrain,
} from './crewingAnalyticsStaticData';
import type { HoursGroup, HoursCategoryRow, PagedResponse } from './crewingAnalyticsApi';
import type { AsyncState } from './useCrewingAnalytics';

type AsyncStateOf<T> = AsyncState<T>;

interface HoursBreakdownProps {
  drillLevel: ChartDrillLevel;
  timeframe: ChartTimeframeGrain;
  selectedRegions?: string[];
  selectedMarkets?: string[];
  selectedLocations?: string[];
  selectedColumnKeys?: string[];
  apiDirectIndirect?: AsyncStateOf<HoursGroup[]>;
  apiOtherFixed?: AsyncStateOf<HoursGroup[]>;
  apiHoursCategoryRows?: AsyncStateOf<PagedResponse<HoursCategoryRow> & { totals?: Record<string, number | null> }>;
  onHoursCategoryColumnsChange?: (dimensionKeys: string[]) => void;
  hoursCategoryTablePage?: number;
  hoursCategoryTablePageSize?: number;
  onHoursCategoryTablePageChange?: (page: number) => void;
  hoursCategoryTablePageSizeOptions?: number[];
  onHoursCategoryTablePageSizeChange?: (pageSize: number) => void;
  hoursCategoryTableSortBy?: string | null;
  hoursCategoryTableSortDirection?: 'asc' | 'desc';
  onHoursCategoryTableSortChange?: (columnKey: string) => void;
}

const HoursBreakdown: React.FC<HoursBreakdownProps> = ({
  drillLevel,
  timeframe,
  selectedRegions = [],
  selectedMarkets = [],
  selectedLocations = [],
  selectedColumnKeys,
  apiDirectIndirect,
  apiOtherFixed,
  apiHoursCategoryRows,
  onHoursCategoryColumnsChange,
  hoursCategoryTablePage = 0,
  hoursCategoryTablePageSize = 25,
  onHoursCategoryTablePageChange,
  hoursCategoryTablePageSizeOptions,
  onHoursCategoryTablePageSizeChange,
  hoursCategoryTableSortBy,
  hoursCategoryTableSortDirection,
  onHoursCategoryTableSortChange,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // Helper to render loading state
  const renderLoading = () => (
    <View style={[styles.loadingContainer, isDark && { backgroundColor: '#202020' }]}>
      <ActivityIndicator size="large" color="#0B5FC6" />
      <Text style={[styles.loadingText, isDark && { color: '#B0B0B0' }]}>Loading data...</Text>
    </View>
  );

  // Helper to render error state
  const renderError = (title: string, error: string) => (
    <View style={[styles.errorContainer, isDark && { backgroundColor: '#3A1414', borderLeftColor: '#C0392B' }]}>
      <Text style={[styles.errorTitle, isDark && { color: '#F28B82' }]}>{title}</Text>
      <Text style={[styles.errorText, isDark && { color: '#B0B0B0' }]}>{error || 'Unable to load data'}</Text>
    </View>
  );

  // Helper to render empty state
  const renderEmpty = (title: string) => (
    <View style={[styles.emptyContainer, isDark && { backgroundColor: '#202020' }]}>
      <Text style={[styles.emptyTitle, isDark && { color: '#B0B0B0' }]}>{title}</Text>
      <Text style={[styles.emptyText, isDark && { color: '#6E6E6E' }]}>No data available</Text>
    </View>
  );

  // Render chart row (Direct/Indirect & Other/Fixed)
  const renderChartRow = () => {
    const diLoading = apiDirectIndirect?.loading;
    const diError = apiDirectIndirect?.error;
    const diData = apiDirectIndirect?.data;

    const ofLoading = apiOtherFixed?.loading;
    const ofError = apiOtherFixed?.error;
    const ofData = apiOtherFixed?.data;

    if (diLoading || ofLoading) return renderLoading();
    if (diError) return renderError('Direct / Indirect Hours', diError);
    if (ofError) return renderError('Other / Fixed Hours', ofError);

    if (!diData || diData.length === 0) return renderEmpty('Direct / Indirect Hours');
    if (!ofData || ofData.length === 0) return renderEmpty('Other / Fixed Hours');

    const toGroupFmt = (groups: HoursGroup[]) =>
      groups.map((g) => ({ label: g.label, values: { plm: g.values.plmRecommended, forecasted: g.values.forecasted, scheduled: g.values.scheduled, actual: g.values.actual } }));

    const directIndirectData = toGroupFmt(diData);
    const otherFixedData = toGroupFmt(ofData);

    return (
      <View style={[styles.chartRow, Platform.OS === 'web' ? null : styles.stackMobile]}>
        <View style={styles.chartCell}>
          <HoursGroupedBarCard
            title="Direct / Indirect Hours"
            unitLabel="hours"
            series={[
              { key: 'plm', label: 'PLM Recommended Hours', color: '#40A4CC' },
              { key: 'forecasted', label: 'Forecasted Hours', color: '#3D6EB3' },
              { key: 'scheduled', label: 'Scheduled Hours', color: '#0A7F10' },
              { key: 'actual', label: 'Actual Hours', color: '#FF8A00' },
            ]}
            groups={directIndirectData}
          />
        </View>
        <View style={styles.chartCell}>
          <HoursGroupedBarCard
            title="Other / Fixed Hours"
            unitLabel="hours"
            series={[
              { key: 'forecasted', label: 'Forecasted Hours', color: '#3D6EB3' },
              { key: 'scheduled', label: 'Scheduled Hours', color: '#0A7F10' },
              { key: 'actual', label: 'Actual Hours', color: '#FF8A00' },
            ]}
            groups={otherFixedData}
          />
        </View>
      </View>
    );
  };

  // Render table
  const renderTable = () => {
    const tableLoading = apiHoursCategoryRows?.loading;
    const tableError = apiHoursCategoryRows?.error;
    const tableData = apiHoursCategoryRows?.data;

    if (tableLoading) return renderLoading();
    if (tableError) return renderError('Hours by Category', tableError);

    if (!tableData || !tableData.rows || tableData.rows.length === 0) {
      return renderEmpty('Hours by Category');
    }

    return (
      <HoursByCategoryTable
        title="Hours vs Scheduled by Category"
        note="Category renders as a tag"
        selectedColumnKeys={selectedColumnKeys as any}
        onColumnsChange={onHoursCategoryColumnsChange}
        backendTotals={tableData.totals as any}
        total={tableData.total}
        page={hoursCategoryTablePage}
        pageSize={hoursCategoryTablePageSize}
        onPageChange={onHoursCategoryTablePageChange}
        pageSizeOptions={hoursCategoryTablePageSizeOptions}
        onPageSizeChange={onHoursCategoryTablePageSizeChange}
        pageLoading={tableLoading}
        sortBy={hoursCategoryTableSortBy}
        sortDirection={hoursCategoryTableSortDirection}
        onSortChange={onHoursCategoryTableSortChange}
        rows={tableData.rows.map((row) => ({
          ...row,
          forecastedHours: row.forecastedHours ?? 0,
          estimatedHours: row.estimatedHours ?? 0,
          size: row.size ?? '',
          actualCases: row.actualCases ?? 0,
          routes: row.routes ?? 0,
          siteSqft: row.siteSqft ?? 0,
        }))}
      />
    );
  };

  return (
    <View style={styles.container}>
      {renderChartRow()}
      {renderTable()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  loadingContainer: {
    padding: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    minHeight: 200,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  errorContainer: {
    padding: 20,
    backgroundColor: '#fff3e0',
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#ff9800',
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#d32f2f',
  },
  errorText: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
  },
  emptyContainer: {
    padding: 30,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    borderRadius: 8,
    minHeight: 150,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#999',
  },
  emptyText: {
    fontSize: 12,
    color: '#bbb',
    marginTop: 4,
  },
  chartRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  stackMobile: {
    flexDirection: 'column',
  },
  chartCell: {
    flex: 1,
    minHeight: 360,
    overflow: 'hidden',
    maxWidth: '100%',
    maxHeight: 420,
  },

});

export default HoursBreakdown;
